import React, { useState } from "react";
import { SectionModel, Page } from "./types";

interface Props {
  sections: SectionModel[];
  companyId: string;
  navigate: (page: Page) => void;
  /** WaiterHomePage'deki SectionService üzerinden yenileme tetikler */
  onRefresh: () => Promise<void>;
}

// Sadece görsel — gerçek CRUD SectionService üzerinden yapılır.
// Bu bileşen bölümleri listeler ve "Yenile" ile WaiterHomePage'in
// initialLoad()'unu tetikler.

const SECTION_COLORS = [
  "#F59E0B",
  "#22C55E",
  "#3B82F6",
  "#8B5CF6",
  "#F43F5E",
  "#14B8A6",
];
const SECTION_BG = [
  "#FFFBEB",
  "#F0FDF4",
  "#EFF6FF",
  "#F5F3FF",
  "#FFF1F2",
  "#F0FDFA",
];
const SECTION_BORDER = [
  "#FDE68A",
  "#BBF7D0",
  "#BFDBFE",
  "#DDD6FE",
  "#FECDD3",
  "#99F6E4",
];

const WaiterSections: React.FC<Props> = ({ sections, navigate, onRefresh }) => {
  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = async () => {
    setRefreshing(true);
    await onRefresh();
    setRefreshing(false);
  };

  return (
    <div
      style={{
        fontFamily: "'Nunito', sans-serif",
        background: "#FAFAF9",
        minHeight: "100%",
        paddingBottom: 32,
      }}
    >
      {/* Header */}
      <div
        style={{
          background: "#1C1917",
          color: "#fff",
          padding: "12px 16px",
          display: "flex",
          alignItems: "center",
          gap: 12,
          position: "sticky",
          top: 0,
          zIndex: 50,
        }}
      >
        <button
          onClick={() => navigate({ name: "WaiterDashboard" })}
          style={{
            width: 36,
            height: 36,
            background: "#292524",
            border: "1px solid #44403C",
            borderRadius: 12,
            color: "#fff",
            fontSize: 16,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          ←
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 800, fontSize: 14 }}>Bölüm Yönetimi</div>
          <div style={{ fontSize: 11, color: "#A8A29E", fontWeight: 600 }}>
            {sections.length} bölüm tanımlı
          </div>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          style={{
            background: "#F59E0B",
            color: "#1C1917",
            fontWeight: 900,
            fontSize: 12,
            padding: "7px 14px",
            borderRadius: 10,
            border: "none",
            cursor: refreshing ? "not-allowed" : "pointer",
            opacity: refreshing ? 0.7 : 1,
          }}
        >
          {refreshing ? "⏳" : "↺ Yenile"}
        </button>
      </div>

      {/* Bilgi notu */}
      <div
        style={{
          margin: "14px 16px 0",
          background: "#FEF3C7",
          borderRadius: 12,
          padding: "10px 14px",
          border: "1px solid #FDE68A",
          display: "flex",
          gap: 8,
          alignItems: "flex-start",
        }}
      >
        <span style={{ fontSize: 16, flexShrink: 0 }}>ℹ️</span>
        <span
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: "#92400E",
            lineHeight: 1.5,
          }}
        >
          Bölüm eklemek veya silmek için yönetici panelini kullanın. Bu ekran
          bölüm listesini gösterir ve yenilemenizi sağlar.
        </span>
      </div>

      {/* Bölüm listesi */}
      <div
        style={{
          padding: "14px 16px",
          display: "flex",
          flexDirection: "column",
          gap: 10,
        }}
      >
        <div
          style={{
            fontSize: 10,
            fontWeight: 900,
            color: "#A8A29E",
            textTransform: "uppercase",
            letterSpacing: 1,
            marginBottom: 4,
          }}
        >
          Mevcut Bölümler
        </div>
        {sections.length === 0 && (
          <div
            style={{
              textAlign: "center",
              padding: "40px 0",
              color: "#A8A29E",
              fontSize: 13,
              fontWeight: 700,
            }}
          >
            Henüz bölüm tanımlı değil.
          </div>
        )}
        {sections.map((sec, idx) => {
          const dotColor = SECTION_COLORS[idx % SECTION_COLORS.length];
          const bg = SECTION_BG[idx % SECTION_BG.length];
          const border = SECTION_BORDER[idx % SECTION_BORDER.length];
          return (
            <div
              key={sec.id}
              style={{
                background: "#fff",
                borderRadius: 16,
                border: `1.5px solid ${border}`,
                padding: "14px",
                display: "flex",
                alignItems: "center",
                gap: 12,
              }}
            >
              {/* Logo */}
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: 12,
                  background: bg,
                  border: `1px solid ${border}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  overflow: "hidden",
                }}
              >
                <img
                  src={
                    sec.logo
                      ? ((sec.logo as any).toApiImage?.() ?? sec.logo)
                      : "/restaurant.png"
                  }
                  alt={sec.name}
                  style={{
                    width: 36,
                    height: 36,
                    objectFit: "cover",
                    borderRadius: 8,
                  }}
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = "/restaurant.png";
                  }}
                />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{ fontWeight: 900, fontSize: 14, color: "#1C1917" }}
                >
                  {sec.name}
                </div>
                <div
                  style={{
                    fontSize: 11,
                    color: "#A8A29E",
                    fontWeight: 600,
                    marginTop: 2,
                  }}
                >
                  ID: {sec.id} · Şirket: {sec.companyId}
                </div>
              </div>
              {/* Renk noktası */}
              <div
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  background: dotColor,
                  flexShrink: 0,
                }}
              />
            </div>
          );
        })}
      </div>

      {/* Hızlı navigasyon */}
      <div style={{ padding: "0 16px" }}>
        <div
          style={{
            fontSize: 10,
            fontWeight: 900,
            color: "#A8A29E",
            textTransform: "uppercase",
            letterSpacing: 1,
            marginBottom: 8,
          }}
        >
          Hızlı Erişim
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {sections.map((sec) => (
            <button
              key={sec.id}
              onClick={() =>
                navigate({ name: "WaiterTables", sectionId: sec.id })
              }
              style={{
                background: "#fff",
                borderRadius: 14,
                border: "1px solid #E7E5E4",
                padding: "12px 16px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                cursor: "pointer",
                width: "100%",
              }}
            >
              <span style={{ fontWeight: 800, fontSize: 13, color: "#1C1917" }}>
                {sec.name}
              </span>
              <span style={{ fontSize: 12, color: "#A8A29E", fontWeight: 700 }}>
                Masalara Git →
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default WaiterSections;
